import { useEffect, useEffectEvent, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Nav, Row, Tab } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { Group, GroupingFactorDataDriven, GroupingFactorWithGroups } from "../ars_ldm";
import { useARSContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ARSText } from "../inputs/ARSText";
import { ARSVariable } from "../inputs/ARSVariable";
import { isGroupingFactor } from "../typeGuards/isGroupingFactor";
import { isGroupingFactorDataDriven } from "../typeGuards/isGroupingFactorDataDriven";
import { isGroupingFactorWithGroups } from "../typeGuards/isGroupingFactorWithGroups";
import { GroupControl } from "./GroupControl";
import { NamedObjectDisplay } from "./NamedObjectDisplay";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the GroupingFactorControl component.
 */
export interface GroupingFactorControlProps {
  /** RFC 6901 JSON Pointer path to the GroupingFactor in ARSContext state */
  dataPath: string;
  /** Can rename the object */
  canRename?: boolean;
}

/**
 * A component for managing GroupingFactor entities with full CRUD capabilities.
 *
 * This component provides:
 * - NamedObjectDisplay for editing name, description, and label
 * - ARSVariable for selecting grouping dataset and variable
 * - Checkbox for the dataDriven boolean field
 * - Management of nested Group entities using GroupControl
 * - RemoveButton for deleting the grouping factor
 *
 * When dataDriven is true, the groups section is disabled as groups are
 * determined automatically from data values.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the GroupingFactor in ARSContext state.
 * @param props.canRename - Optional flag to allow renaming the object.
 * @returns A React functional component rendering the grouping factor editor.
 */
export const GroupingFactorControl = ({
  dataPath,
  canRename,
}: GroupingFactorControlProps): React.ReactElement => {
  const { state, dispatch } = useARSContext();
  const [activeTab, setActiveTab] = useState<string>("named-object");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Get the current grouping factor from state
  const groupingFactor = getFromDataPath(dataPath, state, isGroupingFactor);
  const dataDriven = isGroupingFactorDataDriven(groupingFactor);
  const groups: Group[] = useMemo(
    () => (isGroupingFactorWithGroups(groupingFactor) ? groupingFactor.groups : []),
    [groupingFactor],
  );

  /**
   * Event handler to update the active tab.
   */
  const updateActiveTab = useEffectEvent((newTab: string) => {
    setActiveTab(newTab);
  });

  /**
   * Change the active group tab if it no longer exists
   */
  useEffect(() => {
    if (
      activeTab.startsWith("group-") &&
      groups.length > 0 &&
      parseInt(activeTab.split("-")[1], 10) >= groups.length
    ) {
      const lastGroupIndex = groups.length - 1;
      updateActiveTab(`group-${lastGroupIndex}`);
    } else if (activeTab.startsWith("group-") && groups.length === 0) {
      updateActiveTab("named-object");
    }
  }, [groups.length, dataDriven, activeTab]);

  /**
   * Handles showing the confirmation modal when toggling dataDriven.
   */
  const handleDataDrivenChange = (): void => {
    setShowConfirmModal(true);
  };

  /**
   * Confirms the dataDriven toggle after user acknowledgement.
   */
  const confirmDataDrivenChange = (): void => {
    /* istanbul ignore else */
    if (groupingFactor) {
      let newGroupingFactor: GroupingFactorWithGroups | GroupingFactorDataDriven;
      if (isGroupingFactorDataDriven(groupingFactor)) {
        newGroupingFactor = {
          id: groupingFactor.id,
          name: groupingFactor.name,
          description: groupingFactor.description,
          label: groupingFactor.label,
          dataDriven: false,
          groups: [],
        };
      } else {
        newGroupingFactor = {
          id: groupingFactor.id,
          name: groupingFactor.name,
          description: groupingFactor.description,
          label: groupingFactor.label,
          groupingDataset: "",
          groupingVariable: "",
          dataDriven: true,
          dataValues: [],
        };
      }

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath,
        value: newGroupingFactor,
      });
    }
    setShowConfirmModal(false);
  };

  /**
   * Handles adding a new group to the groups array.
   */
  const handleAddGroup = (): void => {
    /* istanbul ignore else */
    if (isGroupingFactorWithGroups(groupingFactor)) {
      const newGroup: Group = {
        id: crypto.randomUUID(),
        name: `Group_${groups.length + 1}`,
        label: `Group ${groups.length + 1}`,
        description: "",
        condition: {},
        level: 0,
        order: groups.length,
      };

      const newGroupingFactor: GroupingFactorWithGroups = {
        id: groupingFactor.id,
        name: groupingFactor.name,
        description: groupingFactor.description,
        label: groupingFactor.label,
        dataDriven: false,
        groups: [...groups, newGroup],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}`,
        value: newGroupingFactor,
      });

      setActiveTab(`group-${groups.length}`);
    }
  };

  return (
    <>
      <div className="border rounded p-3 mb-3 position-relative">
        {canRename && (
          <div className="position-absolute top-0 end-0 m-2">
            <RemoveArsItemButton dataPath={dataPath} />
          </div>
        )}
        <Tab.Container
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
        >
          <Nav
            variant="tabs"
            className="mb-3"
          >
            <Nav.Item>
              <Nav.Link eventKey="named-object">Properties</Nav.Link>
            </Nav.Item>
            {dataDriven ? (
              <Nav.Item>
                <Nav.Link eventKey="dataset-variable">Dataset/Variable</Nav.Link>
              </Nav.Item>
            ) : (
              groups.map((group, index) => (
                <Nav.Item key={group.id}>
                  <Nav.Link eventKey={`group-${index}`}>{group.name}</Nav.Link>
                </Nav.Item>
              ))
            )}
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="named-object">
              <NamedObjectDisplay
                dataPath={dataPath}
                canRename={canRename}
              />
              <Form.Group
                controlId={`${dataPath.replace(/\//g, "-")}-data-driven`}
                as={Row}
                className="my-1"
                title="Data driven"
              >
                <Form.Label
                  column
                  md={2}
                >
                  Data driven
                </Form.Label>
                <Col
                  md={10}
                  className="d-flex align-items-center justify-content-start"
                >
                  <Form.Check
                    type="switch"
                    checked={dataDriven}
                    onChange={handleDataDrivenChange}
                  />
                </Col>
              </Form.Group>
              <Form.Group
                controlId={`${dataPath.replace(/\//g, "-")}-add-group`}
                className="mt-3"
              >
                <AsyncButton
                  id={`${dataPath.replace(/\//g, "-")}-add-group-btn`}
                  onClick={handleAddGroup}
                  className="d-flex align-items-center btn-sm btn-success"
                  disabled={dataDriven}
                >
                  <span className="me-2">Add group</span>
                  <PlusCircle />
                </AsyncButton>
              </Form.Group>
            </Tab.Pane>
            {dataDriven ? (
              <Tab.Pane eventKey="dataset-variable">
                <ARSVariable
                  datasetDataPath={`${dataPath}/groupingDataset`}
                  variableDataPath={`${dataPath}/groupingVariable`}
                  id={`${dataPath.replace(/\//g, "-")}-grouping-variable`}
                  label="Grouping variable"
                  labelWidth={2}
                  placeHolder="Select dataset.variable"
                />
                <ARSText
                  dataPath={`${dataPath}/dataValues`}
                  id={`${dataPath.replace(/\//g, "-")}-data-values`}
                  label="Data values"
                  labelWidth={2}
                  isArray
                  type="textarea"
                  debounceMilliseconds={2000}
                  rows={5}
                />
              </Tab.Pane>
            ) : (
              groups.map((group, index) => (
                <Tab.Pane
                  key={group.id}
                  eventKey={`group-${index}`}
                >
                  <div className="position-relative">
                    <div
                      className="position-absolute top-0 end-0 mt-2 me-2"
                      style={{ zIndex: 1 }}
                    >
                      <RemoveArsItemButton dataPath={`${dataPath}/groups/${index}`} />
                    </div>
                    <GroupControl dataPath={`${dataPath}/groups/${index}`} />
                  </div>
                </Tab.Pane>
              ))
            )}
          </Tab.Content>
        </Tab.Container>
      </div>
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm change</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Changing the Data driven setting will result in loss of any existing changes.</p>
          <p>Are you sure you want to proceed?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmDataDrivenChange}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

GroupingFactorControl.displayName = "GroupingFactorControl";
